import axios from 'axios';
import { ApiError, ERROR_TYPES } from './errorTypes';
import { AccountInfoData } from 'types';
import {
  entitlementFarmMatches,
  normalizeEntitlementFarmId,
  resolveEntitlementFarmIdFromAccount,
} from 'utils/entitlementFarm';
import { getStoredFarmContext } from 'farmContext';

export const authBaseUrl = (): string => {
  const url = process.env.REACT_APP_AUTH_URL;
  if (!url) {
    throw new Error('REACT_APP_AUTH_URL is not configured');
  }
  return url.replace(/\/$/, '');
};

export type AuthAccountInfoData = AccountInfoData & {
  username?: string;
};

const mapAuthAccountInfo = (payload: Record<string, unknown>): AuthAccountInfoData => {
  const email = typeof payload.email === 'string' ? payload.email : undefined;

  return {
    ...(payload as AccountInfoData),
    username: typeof payload.username === 'string' ? payload.username : email,
  };
};

export const fetchAuthAccountInfo = async (
  token?: string | null,
  farmId?: string | number | null,
): Promise<AuthAccountInfoData> => {
  try {
    const normalizedFarmId = farmId != null && String(farmId).trim() !== '' ? String(farmId).trim() : undefined;
    const response = await axios.get(`${authBaseUrl()}/auth/account-info`, {
      params: normalizedFarmId ? { farmId: normalizedFarmId } : undefined,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      timeout: 10000,
    });

    const payload = response.data?.payload ?? response.data;

    if (!payload || typeof payload !== 'object') {
      throw new ApiError('Invalid account-info response', ERROR_TYPES.GENERIC, response.status, response);
    }

    return mapAuthAccountInfo(payload as Record<string, unknown>);
  } catch (error: any) {
    const response = error?.response;

    if (response?.status === 401) {
      throw new ApiError(
        response?.data?.message || response?.data?.error || 'Unauthorized',
        ERROR_TYPES.GENERIC,
        401,
        response,
      );
    }

    if (response) {
      throw new ApiError(
        response?.statusText || 'Account info request failed',
        ERROR_TYPES.GENERIC,
        response.status,
        response,
      );
    }

    throw error;
  }
};

/** Session farm first, then selected / single farm from account-info bootstrap. */
export const fetchAuthAccountInfoForWorkingFarm = async (
  token?: string | null,
  preferredFarmId?: string | number | null,
): Promise<AuthAccountInfoData> => {
  const sessionFarm = normalizeEntitlementFarmId(getStoredFarmContext().farmId);
  const explicit =
    normalizeEntitlementFarmId(preferredFarmId) ?? sessionFarm;

  if (explicit) {
    const scoped = await fetchAuthAccountInfo(token, explicit);
    if (entitlementFarmMatches(explicit, scoped)) {
      return scoped;
    }
    return fetchAuthAccountInfo(token, explicit);
  }

  const bootstrap = await fetchAuthAccountInfo(token);
  const resolved = resolveEntitlementFarmIdFromAccount(bootstrap);
  if (!resolved) {
    return bootstrap;
  }

  if (entitlementFarmMatches(resolved, bootstrap)) {
    return bootstrap;
  }

  return fetchAuthAccountInfo(token, resolved);
};
